import { useEffect, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import {Typography, Backdrop, CircularProgress} from "@mui/material";
import { grey } from "@mui/material/colors";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isNull, size, isEmpty, isEqual  } from "lodash";

import userService from "@plannel/services/system/user-service";
import authService from "@plannel/services/system/auth-service";
import siteService from "@plannel/services/data/site-master-service";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import CustomHeaderGroup from "@plannel/components/aggrid/CustomHeaderGroup";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import { AddButton, RemoveButton, EditButton, UnlockButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import UserModal from "./UserModal";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import CodeUtil from "../utils/code-util";
import planConfigService from "@plannel/services/data/plan-config-service";

const { snackBarMessageColor } = ColorUtil;
const UserMgmt = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const { IP, RP, DP, MP } = CodeUtil.module;
  const IP_RP = `${IP}_${RP}`;
  /* 사이트(Site) 권한은 IP/RP/MP 모듈에서만 쓴다. */
  const SITE_MODULES = [IP, RP, MP];
  const [backDropOpen, setBackDropOpen] = useState(false);
  const { ACCESS_CONTROL, APPROVAL_ACCESS_CONTROL } = CodeUtil.dpConfig;
  const USER_ACCESS_CONTROL = "USER_ACCESS_CONTROL";
  const [siteAccessControl, setSiteAccessControl] = useState([]);
  const [postnAccessControl, setPostnAccessControl] = useState([]);
  const [approvalAccessControl, setApprovalAccessControl] = useState([]);
  const [siteToggled, setSiteToggled] = useState(false);
  const [postnToggled, setPostnToggled] = useState(false);

  const pageSizes = [100, 200, 500];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.page ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const username = useRef(reduxViewState?.searchCode?.username ?? "");
  const displayName = useRef(reduxViewState?.searchCode?.displayName ?? "");
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  
  const gridRef = useRef();
  const duplicatePostnNames = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);

  const [newUser, setNewUser] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [postn, setPostn] = useState([]);
  const [site, setSite] = useState([]);

  /* /api/user/roles 에는 ROLE_APP_* 이 없다 → 로그인 사용자(localStorage user.roles)의 ROLE_APP_IP/RP/MP 로 모듈 가입 여부를 본다. */
  const appRoles = authService.getCurrentUser()?.roles ?? [];
  const hasSiteModule = SITE_MODULES.some(moduleCd => appRoles.includes(`ROLE_APP_${moduleCd}`));

  const defaultUserInfo = { username: "", password: "", firstName: "", lastName: "", dispName: "", deptName: "", postn: {}, roles: [], site: [], activeFlg: true, delFlg: false };
  const [oldSelectedUser, setOldSelectedUser] = useState({});
  const [selectedUser, setSelectedUser] = useState(defaultUserInfo);

  const resetEditUserInfo = () => {
    const userRoles = isEmpty(roles) ? [] : [roles[1]];
    const resetUser = { ...defaultUserInfo, roles: userRoles, postn: null, site: null };

    setSelectedUser(resetUser);
    setOldSelectedUser(resetUser);
  }

  const getRequestParams = () => {
    const params = {};

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    if (resultTotalCount.current) {
      params.totalCount = resultTotalCount.current;
    }

    params.username = username.current;
    params.dispName = displayName.current;

    return { ...params, ...sortParams };
  }

  const retrieveAllAccessControl = () => {
    planConfigService.getAllAccessControl()
        .then(response => {
            const { data } = response;
            if (data) {
              setSiteAccessControl(data.find(item => item.moduleCd === IP_RP && item.configCd === USER_ACCESS_CONTROL) ?? []);
              setPostnAccessControl(data.find(item => item.moduleCd === DP && item.configCd === ACCESS_CONTROL) ?? []);
              setApprovalAccessControl(data.find(item => item.moduleCd === DP && item.configCd === APPROVAL_ACCESS_CONTROL) ?? []);
            }
        })
        .catch(e => {
            debug(e);
        })
  }

  // position access control, dependent approval access control
  const getApprovalAccessControl = (e) => {
    let approval = {};
    if (size(approvalAccessControl) > 0) {
      approval =  {...approvalAccessControl }
      approval.boolVal = e.target.checked;
    } else {
      approval = {
        activeFlg: true,
        entityName: "PlanConfig",
        boolVal: e.target.checked,
        moduleCd: "DP",
        configCd: APPROVAL_ACCESS_CONTROL
      }
    }
    return approval;
  }

  const switchToggled = (e, isPostn) => {
    setBackDropOpen(true);
    const configDto = [];
    let params = {};
    const accessControl = isPostn ? postnAccessControl : siteAccessControl;
    if (size(accessControl) > 0) {
      params = { ...accessControl }
      params.boolVal = e.target.checked;

      if (isPostn) {
        const approval = getApprovalAccessControl(e);
        configDto.push(approval);
      }

    } else {
      params = {
        activeFlg: true,
        entityName: "PlanConfig",
        boolVal: e.target.checked
      }
      params.moduleCd = isPostn ? DP : IP_RP;
      params.configCd = isPostn ? ACCESS_CONTROL : USER_ACCESS_CONTROL;

      if (isPostn) {
        const approval = getApprovalAccessControl(e);
        configDto.push(approval);
      }

  }
    configDto.push(params);
    
    planConfigService.upsert([...configDto])
      .then(response => {
        if (response.data) {
          if (isPostn) { 
            setPostnAccessControl(response.data[0]);
          } else {
            setSiteAccessControl(response.data[0]);
          }
        }
      })
      .catch(error => {
        debug(error);
      })
      .finally(() => {
        setBackDropOpen(false);
        retrieveAllAccessControl();
      });

    if (isPostn) {
      setPostnToggled(e.target.checked);
    } else {
      setSiteToggled(e.target.checked);
    }
  }

  const handleSwitchClick = (e, isPostn) => {
    e.preventDefault();
    const controlName = isPostn ? "postnAccessControl" : "siteAccessControl";
    const currentState = isPostn ? postnToggled : siteToggled;

    setDialogInfo({
      open: true, title: "confirm", actionName: isPostn ? "POSTN_SWITCH" : "SITE_SWITCH",
      content: currentState ? t("MSG_ChangeAccessControlOff", { control: t(controlName)}) : t("MSG_ChangeAccessControlOn", { control: t(controlName)}) });
    }

  const retrievePosition = () => {
    userService.getUserPostn()
      .then(response => {
        duplicatePostnNames.current = response.data.map(position => position.name).filter((name, index, names) => names.indexOf(name) !== index);
        setPostn(response.data);
        gridRef.current?.api?.refreshCells({ columns: ["postnNm"], force: true });
      })
      .catch(error => { debug(error); });
  }

  const retrieveSite = () => {
    siteService.getAll({ pagination: false })
      .then(response => {
        if (response?.data?.results) {
          setSite(response.data.results);
        }
      })
      .catch(error => { debug(error); });
  }

  const retrieveRoles = () => {
    userService.getUserRoles()
      .then(response => {
        setRoles(response.data);
      })
      .catch(error => { debug(error); });
  }

  const retrieveUsers = () => {
    resetEditUserInfo();
    setUsers([]);

    const params = getRequestParams();
    userService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setUsers(results);
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      })
      .then(() => {
        const searchCode = { username: username.current, displayName: displayName.current };
        const reduxState = reduxUtil.createViewState(viewName, searchCode, currentPage, pageSize);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const saveUser = () => {
    if (selectedUser?.username || !isEmpty(selectedUser.username)) {
      userService.upsert(selectedUser)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          setModalOpen(false);
          retrieveUsers();
        })
        .catch(error => {
          const { message, details } = error.response.data;

          let errorMsg = message ? t(message) : t("MSG_SaveFailed");
          if (details?.count && (isEqual(message, "MSG_PasswordDCredit") || isEqual(message, "MSG_PasswordLCredit") || isEqual(message, "MSG_PasswordOCredit") ||
            isEqual(message, "MSG_PasswordUCredit") || isEqual(message, "MSG_PasswordRepeat"))) {
            errorMsg = t(message, { N: details.count });
          }
          if (details?.min && details?.max && isEqual(message, "MSG_CheckPasswordLength")) {
            errorMsg = t(message, { min: details.min, max: details.max });
          }

          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: errorMsg });
          debug(error);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const addUser = () => {
    resetEditUserInfo();
    setNewUser(true);
    setModalOpen(true);
  }

  const deleteUser = () => {
    const userIds = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        userIds.push(data.id);
      }
    );

    if (userIds.length > 0) {
      userService.deleteUser(userIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(error => {
          const { message } = error.response.data;
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(message) || t("MSG_DeleteFailed") });
          debug(error);
        })
        .then(() => {
          // row remove or reload
          retrieveUsers();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedUser") });
    }
  }

  const editUser = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (isEmpty(selectedRows)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedUser") });
    } else {
      if (selectedRows.length > 1) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_SelectOneUser") });
      } else {
        const selectedRowData = selectedRows[0];
        selectedRowData.position = {};
        selectedRowData.userRole = {};
        selectedRowData.siteObj = {};
        Object.keys(selectedRowData).forEach(key => {
          if (isNull(selectedRowData[key])) {
            selectedRowData[key] = "";
          }
        });

        setSelectedUser({
          ...selectedUser,
          ...selectedRowData
        });
        setOldSelectedUser({
          ...selectedUser,
          ...selectedRowData
        });

        setNewUser(false);
        setModalOpen(true);
      }
    }
  }

  const unlockUser = () => {
    userService.unlockUser(selectedUser.username)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_UnlockSuccess") });
        retrieveUsers();
      })
      .catch(error => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_UnlockFailed") });
        debug(error);
      });
  }

  const getPostnLabel = position => duplicatePostnNames.current.includes(position.name) ? `${position.name} (${position.postnCd})` : position.name;

  const lookupValue = (params) => {
    return isEmpty(params.value) ? "" : params.value.join(", ");
  }

  const roleLookupValue = params => {
    const { value } = params;

    if (!value) {
      return "";
    }

    const roleLabels = [];
    for (const item of value) {
      const roleLabel = GridUtils.gridValueL10N(item.name, { headerName: "userRoles" });
      roleLabels.push(roleLabel);
    }

    return roleLabels.join(", ");
  }

  const onSelectionChanged = params => {
    const selectedRows = params.api.getSelectedRows();
    const selected = isEmpty(selectedRows) ? {} : selectedRows[0];
    setSelectedUser(selected);
  }

  const getRowStyle = params => {
    const { acctLockedFlg, activeFlg, delFlg } = params.data;
    if (acctLockedFlg || !activeFlg || delFlg) {
      return { background: grey[300] };
    }

    return null;
  }

  const columnDefs = [
    {
      headerName: "emailAddress", field: "username", type: "nonEditableColumn", width: 250,
      pinned: "left", lockPinned: true
    },
    { headerName: "lastName", field: "lastName", type: "nonEditableColumn", width: 110 },
    { headerName: "firstName", field: "firstName", type: "nonEditableColumn", width: 110 },
    { headerName: "displayName", field: "dispName", type: "nonEditableColumn", width: 150 },
    { headerName: "deptName", field: "deptName", type: "nonEditableColumn", width: 150 },
    { field: "postn", type: "nonEditableColumn", hide: true, suppressColumnsToolPanel: true },
    { headerName: "postn", field: "postnNm", type: "nonEditableColumn", valueGetter: params => (params.data?.postn ?? []).map(getPostnLabel), valueFormatter: lookupValue, sortable: false },
    { field: "site", type: "nonEditableColumn", hide: true, suppressColumnsToolPanel: true },
    { headerName: "site", field: "siteNm", type: "nonEditableColumn", valueFormatter: lookupValue, sortable: false },
    { headerName: "role", field: "roles", type: "nonEditableColumn", valueFormatter: roleLookupValue, flex: 1, minWidth: 220, sortable: false },
    {
      headerName: "detail", groupId: "detail",
      headerGroupComponent: CustomHeaderGroup,
      children: [
        { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn", "nonEditableColumn"], width: 100 },
        { headerName: "delFlg", field: "delFlg", type: ["booleanColumn", "nonEditableColumn"], width: 100 },
        { headerName: "pwdExpiredFlg", field: "pwdExpiredFlg", type: ["booleanColumn", "nonEditableColumn"], width: 110, columnGroupShow: "open" },
        { headerName: "acctLockedFlg", field: "acctLockedFlg", type: ["booleanColumn", "nonEditableColumn"], width: 110, columnGroupShow: "open" },
        { headerName: "mfaFlg", field: "mfaFlg", type: ["booleanColumn", "nonEditableColumn"], width: 110, columnGroupShow: "open" },
        { headerName: "loginFailCount", field: "loginFailCount", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 120, columnGroupShow: "open" },
        { headerName: "loginTotalCount", field: "loginTotalCount", colId: "loginTotalCount", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 110, columnGroupShow: "open" },
        { headerName: "type", field: "loginSsoType", colId: "loginSsoType", type: "nonEditableColumn", width: 100, columnGroupShow: "open" },
        { headerName: "lastLoginTs", field: "lastLoginTs", colId: "lastLoginTs", type: "nonEditableColumn", valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: "lastLogoutTs", field: "lastLogoutTs", colId: "lastLogoutTs", type: "nonEditableColumn", valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: "pwdChangeTs", field: "pwdChangeTs", colId: "pwdChangeTs", type: "nonEditableColumn", valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: "createdTs", field: "createdTs", colId: "createdTs", type: "nonEditableColumn", valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: "createdBy", field: "createdBy", colId: "createdBy", type: "nonEditableColumn", width: 150, columnGroupShow: "open" },
      ]
    },
    { headerName: "updatedTs", field: "updatedTs", colId: "updatedTs", type: "nonEditableColumn", valueFormatter: dateUtils.formatDateTime },
    { headerName: "updatedBy", field: "updatedBy", colId: "updatedBy", type: "nonEditableColumn", width: 150 },
  ];

  const onGridReady = params => {
    retrieveRoles();
    retrievePosition();
    retrieveAllAccessControl();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveUser();
    } else if (action === "DELETE") {
      deleteUser();
    } else if (action === "ACTIVE_CONFIRM") {
      const initLoginInfo = {
        loginFailCount: 0, loginTotalCount: 0, pwdExpiredFlg: false, activeFlg: true, mfaFlg: false,
        acctLockedFlg: false, delFlg: false, loginSsoType: null, lastLoginTs: null, lastLogoutTs: null
      };
      setSelectedUser({
        ...selectedUser,
        ...initLoginInfo
      });
    } else if (action === "POSTN_SWITCH") {
      const syntheticEvent = { target: { checked: !postnToggled } };
      switchToggled(syntheticEvent, true);
    } else if (action === "SITE_SWITCH") {
      const syntheticEvent = { target: { checked: !siteToggled } };
      switchToggled(syntheticEvent, false);
    } else if (action === "CONFIRM") {
      setModalOpen(false);
    } else if (action === "UNLOCK") {
      unlockUser();
    }
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveUsers();
    }
  }

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveUsers();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  /* 사이트 조회는 IP/RP/MP 모듈(ROLE_APP_*)이 있을 때만 한다. 마운트 시점에도 이 effect 가 조회를 맡는다. */
  useEffect(() => {
    if (hasSiteModule) {
      retrieveSite();
    } else {
      setSite([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSiteModule]);

  /* 저장된 컬럼 상태(GridUtils.setColumnDefs initState)가 비동기로 복원되며 다시 보이게 할 수 있어 데이터가 그려질 때마다 맞춘다. */
  useEffect(() => {
    gridRef.current?.api?.setColumnsVisible(["siteNm"], hasSiteModule);
  }, [hasSiteModule, users]);

  useEffect(() => {
    if (!isEmpty(siteAccessControl)) {
      setSiteToggled(siteAccessControl?.boolVal ?? false);
    }
  }, [siteAccessControl]);

  useEffect(() => {
    if (!isEmpty(postnAccessControl)) {
      setPostnToggled(postnAccessControl?.boolVal ?? false);
    }
  }, [postnAccessControl]);

  return (
    <>
      <FilterContainer>
        <TextFieldSearch sx={{ width: 200, minWidth: 100 }} label="emailAddress" searchCode={username} retrieve={initPageToRetrieve} />
        <TextFieldSearch sx={{ ml: 1, width: 200, minWidth: 100 }} label="displayName" searchCode={displayName} retrieve={initPageToRetrieve} />
        <Box sx={{ display: "flex", ml: "auto", minWidth: 110, alignItems: "baseline" }}>
          <Box sx={{ display: "flex", height: 50, width: "100%", minWidth: 300 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mr: 2, ml: "auto", my: "auto" }}>
              {t("postnAccessControl")}
            </Typography>
            <StyledSwitch size="small" sx={{ display: "flex", my: "auto", mr: 2 }} checked={postnToggled} onChange={e => handleSwitchClick(e, true)} />
          </Box>
          {hasSiteModule && (
            <Box sx={{ display: "flex", height: 50, width: "100%" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mr: 2, ml: "auto", my: "auto" }}>
                {t("siteAccessControl")}
              </Typography>
              <StyledSwitch size="small" sx={{ display: "flex", my: "auto", mr: 2 }} checked={siteToggled} onChange={e => handleSwitchClick(e, false)} />
            </Box>
          )}
          <AddButton onClick={addUser} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <EditButton onClick={editUser} />
          <UnlockButton onClick={() => setDialogInfo({ open: true, title: "unlock", content: t("MSG_UnlockConfirm") })} disabled={!selectedUser?.acctLockedFlg} />
        </Box>
      </FilterContainer>
      <Box id="user-mgmt-grid" className="ag-theme-balham grid-area" mb={1} >
        <AgGridReact
          ref={gridRef}
          rowData={users}
          {...DefaultGridSetting({ title, viewName })}
          getRowStyle={getRowStyle}
          rowSelection={{ ...DefaultGridSetting({ title, viewName }).rowSelection, checkboxes: true, headerCheckbox: true, selectAll: 'filtered', enableClickSelection: true }}
          selectionColumnDef={{ pinned: 'left' }}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
          onCellDoubleClicked={editUser}
        >
        </AgGridReact>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <UserModal
        t={t}
        open={modalOpen}
        close={setModalOpen}
        postn={postn}
        sites={site}
        roles={roles}
        oldSelectedUser={oldSelectedUser}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        newUser={newUser}
        setDialogInfo={setDialogInfo}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        actionName={dialogInfo.actionName}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={backDropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default withTranslation()(UserMgmt);